<script setup lang="ts">
  import { ECashierTab } from '~/store/cashier';
  import { useUiStore } from '~/store/ui';

  const emit = defineEmits<{ close: [] }>();
  const { content, handleItemClick, openCashier } = useSidebarLinks(emit);
  const { isMobile } = useDeviceType();

  const { store: tournamentStore } = useTournamentsLoader();
  const tournamentsFeatureEnabled = computed(() => !!tournamentStore.value?.isEnabled);

  const { store: vipStore } = useVipFeature();
  const vipFeatureEnabled = computed(() => !!vipStore.value?.isEnabled);

  const { store: welcomeStore } = useWelcomeFeature();
  const welcomeFeatureAvailable = computed(() => !!welcomeStore.value?.isAvailable);

  function closeSidebar() {
    useUiStore().toggleSidebar(false);
    emit('close');
  }

  function onStateChange(isOpen: boolean) {
    if (isOpen) return;
    useUiStore().toggleSidebar(false);
  }
</script>

<template>
  <USlideover
    close-icon="i-lucide-arrow-right"
    side="left"
    class="max-w-[281px] bg-white/5 backdrop-blur-[25px] px-5 py-4 overflow-y-auto"
    :ui="{ overlay: 'bg-black/75' }"
    @update:open="onStateChange"
  >
    <template #content>
      <aside class="flex flex-col">
        <div class="grid grid-cols-[auto_1fr] items-center h-[50px] mb-4">
          <CoreButton
            :ui="{ leadingIcon: 'size-8' }"
            class="w-[50px] h-[50px] justify-center bg-white/7 cursor-pointer text-white mr-3 p-0"
            icon="mdi:close"
            size="xl"
            variant="ghost"
            @click="closeSidebar"
          />
          <CoreImage
            densities="x1 x2"
            preload
            format="webp"
            alt="maxduel logo"
            class="shrink-0"
            sizes="xs:154px md:242px"
            src="/images/logo.webp"
            :width="isMobile ? 154 : 242"
          />
        </div>
        <div class="w-full bg-white/10 h-[1px] mb-5" />

        <LazyLeaderboardSidebarBanner v-if="tournamentsFeatureEnabled" :is-compact="false" @click="closeSidebar" />
        <LazyVipSidebarSection v-if="vipFeatureEnabled && !welcomeFeatureAvailable" @click="closeSidebar" />
        <LazyWelcomeSidebarWidget v-if="welcomeFeatureAvailable" @click="closeSidebar" />

        <AppUserBalance sidebar />

        <div class="mt-2.5 grid grid-cols-2 gap-1.5 h-[52px]">
          <CoreButton class="justify-center" color="secondary" size="xl" @click="openCashier(ECashierTab.Withdraw)">
            Withdraw
          </CoreButton>
          <CoreButton class="justify-center" color="primary" size="xl" @click="openCashier(ECashierTab.Deposit)">
            Deposit
          </CoreButton>
        </div>
        <div class="w-full bg-white/10 h-[1px] mt-5" />
        <div class="mt-6 font-barlow">
          <div
            v-for="item in content"
            :key="item.key"
            class="rounded-[10px] overflow-hidden select-none cursor-pointer hover:bg-white/1 transition-bg ease-in-out duration-200"
            :class="{ 'bg-white/7': item.isActive }"
            @click="handleItemClick(item)"
          >
            <div v-if="item.isDivider" class="w-full h-[1px] bg-white/10 my-2.5" />
            <div v-else class="relative flex items-center px-1 py-2">
              <div v-if="item.isActive" class="h-full w-1 absolute left-0 bg-primary" />
              <div class="flex items-center justify-center w-[60px] h-10">
                <component
                  :is="`Svgo${item.icon || ''}`"
                  :class="{
                    'text-primary ': item.isActive,
                    'text-white': !item.isActive,
                  }"
                  class="size-5"
                />
              </div>
              <span class="mr-1.5 text-white font-barlow text-base font-medium leading-normal">{{ item.title }}</span>
              <div
                v-if="item.badge?.value"
                class="bg-[#dc0000]"
                :class="{
                  'rounded-full px-1.5': item.badge.variant === 'round',
                  'px-2 py-1 rounded-sm': item.badge.variant === 'square',
                }"
              >
                <div
                  class="font-inter font-black leading-normal text-center"
                  :class="{
                    'text-white text-xs': item.badge.variant === 'round',
                    'text-[#ECECEC] text-[10px]': item.badge.variant === 'square',
                  }"
                >
                  {{ item.badge.value }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </template>
  </USlideover>
</template>
